import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { BaseModal, ModalCloseCross, type BaseModalProps } from "../../../../components/modals/BaseModal";
import { ComunicacionAjax } from "../../../../ts/api/comunicacion";
import type { Usuario } from "../../../../ts/typings/clases";
import { createColumnHelper } from "@tanstack/react-table";
import { useCallback, useMemo } from "react";
import { TypedTable } from "../../../../components/TypedTable";
import { Loading, QueryError } from "../../../../components/Loading/Loading";

interface ModalSeleccionarPersonalProps extends BaseModalProps { readonly idCiclo: number | null; }
export function ModalSeleccionarPersonal(props: ModalSeleccionarPersonalProps) {


	return (
		<BaseModal show={props.show} close={props.close}>
			<div className="modal-header">
				<h4 className="modal-title">Personal</h4>
				<ModalCloseCross onClick={props.close} />
			</div>
			<div className="modal-body" style={{ minWidth: "50vw" }}>
				<div className="content-box big-box">
					<div className="table-responsive">
						<TablaPersonalCiclo idCiclo={props.idCiclo} />
					</div>
				</div>
				<div className="modal-footer">
					<button type="button" className="btnSecondary" onClick={props.close}>Cerrar</button>
				</div>
			</div>
		</BaseModal>
	);
}
function TablaPersonalCiclo(props: { readonly idCiclo: number | null; }) {
	const queryClient = useQueryClient();
	const { data, error, isPending } = UseUsuriosDisponibles();
	const { data: dataPersonal, error: errorPersonal, isPending: isPendingPersonal } = UsePersonalAsignado({
		idCiclo: props.idCiclo ?? null,
	});



	const onSeleccionarPersonal = useCallback((row: Usuario, matchStatus: "match" | "") => {
		if (props.idCiclo == null) {
			alert("Error al asignar el ciclo");
			throw new Error("Error: IdCiclo es null");
		}

		if (matchStatus === "match") {
			void eliminarCicloUsuario(props.idCiclo, Number(row.id), queryClient);
		} else {
			void asignarCicloUsuario(props.idCiclo, Number(row.id), queryClient);
		}
	}, [props.idCiclo, queryClient]);

	const columns = useMemo(() => {
		const columnHelper = createColumnHelper<Usuario>();
		const nom = (linea: Usuario) => linea.nombre + " " + linea.apellido;
		const getRol = (linea: Usuario) => {
			let rol;
			switch (linea.rol as string) {
				case "jefeFabrica": rol = "Jefe de fabrica"; break;
				default: rol = linea.rol;
			}
			return rol;
		};
		const accionSeleccion = (linea: Usuario) => {
			let match: "match" | "" = "";

			dataPersonal?.forEach((personal) => {
				if (personal.representante_id === Number(linea.id)) {
					if (personal.ciclo_id === props.idCiclo) {
						match = "match";
						return true;
					}
				}
				return false;
			});



			return <div className={`seleccionarPersonal ${match}`} onClick={() => onSeleccionarPersonal(linea, match)} />;
		};
		return [
			columnHelper.accessor(nom, { header: "Nombre", meta: { className: "dt-center" } }),
			columnHelper.accessor(getRol, { header: "Rol", meta: { className: "dt-center" } }),
			columnHelper.display({
				id: "acciones",
				header: "",
				cell: ({ row }) => accionSeleccion(row.original),
			})
		];
	}, [dataPersonal, onSeleccionarPersonal, props.idCiclo]);


	if (error || errorPersonal) return <QueryError error={error ?? errorPersonal} fallback="Error al cargar los datos" />;
	if (isPending || isPendingPersonal) return <Loading />;




	return (
		<TypedTable
			className="dataTable display"
			columns={columns}
			data={data}
			pagination
		/>
	);
}


interface PersonalAsignadoParams {
	idCiclo: number | null;
}
function UsePersonalAsignado(params: PersonalAsignadoParams) {
	return useQuery<UsuarioAsignado[]>({
		queryKey: ["getPersonalAsignado", params],
		queryFn: () => {
			if (params.idCiclo == null) return Promise.resolve([]);
			return ComunicacionAjax.sendAjaxRequest<UsuarioAsignado[]>("GET", "/getPersonalAsignado", params);
		},
	});
}
interface UsuarioAsignado {
	ciclo_id: number | null;
	medio_id: number | null;
	pedido_id: number | null;
	representante_id: number;
}
function UseUsuriosDisponibles() {
	return useQuery<Usuario[]>({
		queryKey: ["getCarretillerosAlmacen"],
		queryFn: () => ComunicacionAjax.sendAjaxRequest<Usuario[]>("GET", "/getCarretillerosAlmacen", []),
	});
}




async function asignarCicloUsuario(idCiclo: number, idRepresentante: number, queryClient: QueryClient): Promise<void> {
	const parametro = {
		"idCiclo": idCiclo,
		"idRepresentante": idRepresentante,
	};

	const url = "/asignarCicloUsuario";

	await ComunicacionAjax.sendAjaxRequest("POST", url, parametro);
	const prevData = queryClient.getQueryData<UsuarioAsignado[]>(["getPersonalAsignado", { idCiclo }]);
	if (prevData) {
		const newData = [...prevData, { ciclo_id: idCiclo, medio_id: null, pedido_id: null, representante_id: idRepresentante }];
		queryClient.setQueryData(["getPersonalAsignado", { idCiclo }], newData);
	} else {	//Si podemos usar los datos previos, los actualizamos en cache sin necesidad de recargar, que es una peticion pesada
		await queryClient.invalidateQueries({ queryKey: ["getPersonalAsignado", { idCiclo }] });
	}
	await queryClient.invalidateQueries({ queryKey: ["getCiclosAsignados"] });
}
async function eliminarCicloUsuario(idCiclo: number, idRepresentante: number, queryClient: QueryClient): Promise<void> {
	const parametro = {
		"idCiclo": idCiclo,
		"idRepresentante": idRepresentante,
	};

	const url = "/eliminarCicloUsuario";

	await ComunicacionAjax.sendAjaxRequest("POST", url, parametro);

	const prevData = queryClient.getQueryData<UsuarioAsignado[]>(["getPersonalAsignado", { idCiclo }]);
	if (prevData) {
		const newData = prevData.filter(item => item.representante_id != idRepresentante);
		queryClient.setQueryData(["getPersonalAsignado", { idCiclo }], newData);
	} else {	//Si podemos usar los datos previos, los actualizamos en cache sin necesidad de recargar, que es una peticion pesada
		await queryClient.invalidateQueries({ queryKey: ["getPersonalAsignado", { idCiclo }] });
	}
	await queryClient.invalidateQueries({ queryKey: ["getCiclosAsignados"] });
}